import React, { useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Navigator from "./Navigator";
import Client2DPlayer from "./Client2DPlayer";
import {
  remote_service_client_info,
  remote_service_session_init,
} from "./../utils";
import MuiAppBar from "@mui/material/AppBar";
import LanguageModal from "./LanguageModal";
import content from "./../assets/content.json";

import {
  Button,
  Typography,
  CssBaseline,
  Box,
  GlobalStyles,
  styled,
  Toolbar,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Popup from "./Popup";
import InfoModal from "./InfoModal";

import Complete from "./Complete";
import screenfull from "screenfull";

function Copyright() {
  return (
    <Typography color="#FFC627" fontSize="12px" padding={0}>
      {"Copyright © "}
      {/* <Link color="inherit" href="https://dreamscapeimmersive.com/"> */}
      {new Date().getFullYear()} Dreamscape Learn, Inc.
      {/* </Link>{" "} */}
    </Typography>
  );
}

let theme = createTheme({
  palette: {
    primary: {
      light: "#63ccff",
      main: "#FFC627",
      dark: "#006db3",
    },
  },
  typography: {
    h5: {
      fontWeight: 500,
      fontSize: 40,
      letterSpacing: 0.5,
    },
    fontFamily: ["BlinkMacSystemFont", "sans-serif"].join(","),
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
    },
  },
  mixins: {
    toolbar: {
      textAlign: "center",
      height: "8vh",
      minHeight: 100,
    },
  },
});

theme = {
  ...theme,
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#081627",
          cursor: "default",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        contained: {
          boxShadow: "none",
          "&:active": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          marginLeft: theme.spacing(1),
        },
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
          backgroundColor: theme.palette.common.white,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          margin: "0 16px",
          minWidth: 0,
          padding: 0,
          [theme.breakpoints.up("md")]: {
            padding: 0,
            minWidth: 0,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1),
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 4,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: "rgb(255,255,255,0.15)",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: "#4fc3f7",
          },
        },
      },
    },
    MuiListItem: {
      userSelect: "none",
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 14,
          fontWeight: theme.typography.fontWeightMedium,
          userSelect: "none",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "inherit",
          minWidth: "auto",
          marginRight: theme.spacing(2),
          "& svg": {
            fontSize: 20,
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 24,
          height: 24,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: "#081627",
          backgroundColor: "#FFC627",
          fontWeight: "400",
          fontSize: "0.875rem",
          maxWidth: "210px",
          borderRadius: "8px",
        },
      },
    },
  },
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  boxShadow: `none`,
  backgroundColor: "#000",
  height: `60px`,
  bottom: `0px`,
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    display: 'none',
    height: `60px`,
    bottom: `0px`,
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  })
);
const drawerWidth = 300;

const errorMap = {
  400: "Bad Request: Missing or invalid authentication token, please re-register for a new session or close this window to quit.",
  404: "Not Found: Missing or invalid authentication token, please re-register for a new session or close this window to quit.",
  409: "This Dreamscape Learn session is no longer valid. Please return to Canvas, refresh your page, and start a new session.",
};

const TEST_MODE = process.env.REACT_APP_TEST_MODE === "true";

export default function Paperbase() {
  const handleClick = () => {
    const element = document.querySelector("#furioos_container");
    if (screenfull.isEnabled) {
      screenfull.request(element);
    }
  };

  const [open, setOpen] = useState(true);
  const [warningbar, setWarningbar] = useState(true);
  const [player, setPlayer] = useState(null);
  const [sessionid, setsessionid] = useState("loading...");
  const [firstName, setFirstName] = useState("loading...");
  const [lastName, setLastName] = useState("loading...");
  const [preferredName, setPreferredName] = useState("loading...");
  const [sdkid, setSdkid] = useState();
  const [consumable, setConsumable] = useState(null);
  const [apierror, setApierror] = useState(false);
  const [instruction, setInstruction] = useState(false);
  const [expiryMil, setExpiryMil] = useState();
  const [title, setTitle] = useState("loading...");
  const [payload, setPayload] = useState();
  const [usersessionid, setUsersessionid] = useState();
  const [sessioncomplete, setSessioncomplete] = useState(null);
  const languageList = process.env.REACT_APP_SUPPORTED_LANGUAGES
    ? process.env.REACT_APP_SUPPORTED_LANGUAGES.split(",")
    : ["en-US"];


  const [language, setLanguage] = useState("en-US");
  const [showLanguageModal, setShowLanguageModal] = useState(languageList.length > 1);
  const currentLanguage = content[language]["content"]
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleEndofSession = () => {
    setSessioncomplete(true);
  };

  const handleWarningClose = () => {
    setWarningbar(false);
  };

  const handleInstructionOpen = () => {
    setInstruction(true);
  };

  const handleInstructionClose = () => {
    setInstruction(false);
  };

  const initializeSession = (payload, selectedLang) => {
    remote_service_session_init(payload, selectedLang)
      .then((res) => {
        setsessionid(res.data.session_id);
        setUsersessionid(res.data.user_session_id);
        setPayload(payload);
        return remote_service_client_info(payload, res.data.user_session_id);
      })
      .then((res) => {
        setSdkid(res.data.manifest.client.partner_references.sdk_link_id);
        setConsumable(res.data.manifest.client.client_consumable);
        setFirstName(res.data.user.profile.first_name);
        setLastName(res.data.user.profile.last_name);
        setPreferredName(res.data.user.profile.preferred_name);
        setTitle(res.data.title.shortname);

        const yyyymmdd = res.data.expiry.session.split("T")[0];
        const year = yyyymmdd.split("-")[0];
        const month = Number(yyyymmdd.split("-")[1]) - 1;
        const day = yyyymmdd.split("-")[2];

        const hhmmss = res.data.expiry.session.split("T")[1];
        const hour = hhmmss.split(":")[0];
        const min = hhmmss.split(":")[1];
        const sec = hhmmss.split(":")[2];

        const sessionExpiryUTC = new Date(
          Date.UTC(year, month, day, hour, min, sec)
        );
        let expiry = new Date(sessionExpiryUTC) - new Date();
        setExpiryMil(expiry);
        setPlayer(true);
      })
      .catch((error) => {
        console.error(error.response.data);
        setApierror({
          statusCode: error.response.status,
          errorMessage: errorMap[error.response.status]
            ? error.response.data
            : errorMap[error.response.status],
        });
      });
  };


  const handleLanguageSelect = (lang) => {
    setLanguage(lang);
    setShowLanguageModal(false);

    // Initialize session once language is chosen
    const query = new URLSearchParams(window.location.search);
    const payload = query.get("payload");
    if (payload) {
      initializeSession(payload, lang);
    }else{
      setApierror({
        statusCode: 404,
        errorMessage: errorMap[404]
      });
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const payload = query.get("payload");

    if (TEST_MODE) {
      console.error("no payload supplied in url");
      setExpiryMil(1000 * 60 * 5);
      setConsumable({ payload });
      setPlayer(true);
      return;
    }

    // Auto-initialize only when a single language is available
    if (languageList.length <= 1 && payload) {
      initializeSession(payload, "en-US");
    } else if (!payload){
      setApierror({
        statusCode: 404,
        errorMessage: errorMap[404]
      });
    }

  }, []);

  return (
    <ThemeProvider theme={theme}>
      {showLanguageModal && (
        <LanguageModal
          selectedLang={language}
          setSelectedLang={handleLanguageSelect}
          isOpen={true}
          language={language}
          languageList={languageList}
        />
      )}
      <Box sx={{ display: "flex", minHeight: "100vh", margin: "auto" }}>
        {apierror && (
          <Popup
            title="Error:"
            bodytext={apierror.errorMessage}
            errorcode={apierror.statusCode}
          />
        )}
        {instruction && (
          <InfoModal
            instruction={instruction}
            language={language}
            handleInstructionClose={handleInstructionClose}
          />
        )}
        <CssBaseline />
        <AppBar
          position="fixed"
          open={open}
          sx={{ top: "auto", bottom: 0, width: 60, left: 0 }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label={currentLanguage["open_drawer"]}
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                bottom: 20,
                backgroundColor: "rgba(255, 198, 39, 0.2)",
                "&:hover": { backgroundColor: "#FFC627", color: "#000" },
                color: "#FFC627",
                ...(open && { display: "none" }),
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Navigator
          open={open}
          title={title}
          handleDrawerClose={handleDrawerClose}
          preferredname={preferredName}
          firstname={firstName}
          lastname={lastName}
          sessionid={sessionid}
          expiryMil={expiryMil}
          usersessionid={usersessionid}
          payload={payload}
          sdkid={sdkid}
          handleEndofSession={handleEndofSession}
          handleInstructionOpen={handleInstructionOpen}
          language={language}
        />

        <Main open={open} sx={{ p: 0, backgroundColor: "#000" }}>
          <GlobalStyles styles={{ iframe: { border: "none" } }} />
          {player && !sessioncomplete && (
            <React.Fragment>
              <Client2DPlayer
                expiryMil={expiryMil}
                consumable={consumable}
                language={language}
              />
            </React.Fragment>
          )}

          {sessioncomplete && <Complete />}
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              right: 0,
              opacity: 0.5,
            }}
          >
            <Copyright />
          </Box>
        </Main>
      </Box>
      <React.Fragment>
        <Snackbar
          open={warningbar}
          onClose={handleWarningClose}
          autoHideDuration={10000}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <Alert
            onClose={handleWarningClose}
            severity="warning"
            sx={{ width: "100%" }}
          >
            {currentLanguage['modal_msg']}
          </Alert>
        </Snackbar>
      </React.Fragment>
    </ThemeProvider>
  );
}
